import { useAvailableIncidentActions } from '../shared/hooks/useAvailableIncidentActions'
import { useGridTesting } from '../shared/hooks/useGridTesting'
import { useSetIsIncidentReal } from '../shared/hooks/useSetIsIncidentReal'
import { IncidentTab, useTabs } from '../shared/hooks/useTabs'
import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'
import { useLightMode } from '@/hooks/useLightMode'
import { usePermissions } from '@/hooks/usePermissions'
import { useWindowSize } from '@react-hook/window-size'
import { BiWrench } from 'react-icons/bi'

import { Box, useDisclosure } from '@chakra-ui/react'

import { DarkProvider, NoteIcon, ResolveIcon, SOPIcon } from '@hivewatch/dark'

import { EmergencyCallButton } from '@/features/emergency-call'
import {
  StandaloneGridTestingModal,
  TailgateGridProvider,
} from '@/features/tailgate-grid'
import { IncidentQueryResult } from '@/graphql/generated/hooks'
import {
  DeviceType,
  IncidentStatus,
  IncidentWorkflowStatus,
} from '@/graphql/generated/schemas'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import {
  closeSOPIncidentDrawer,
  closeTraceLogDrawer,
  closeWorkflowDrawer,
  selectSOPIncidentDrawerState,
  toggleSOPIncidentDrawer,
  toggleWorkflowDrawer,
} from '@/redux/ui/uiSlice'
import { GTZ_ENTER } from '@/utils/analytics'

import { AddNote } from '../add-note'
import { AssociateCaseModal } from '../associate-case'
import {
  DarkGuardPanel,
  DispatchGuards,
  GuardActionTab,
  RadioGuards,
} from '../guard-action'
import { DispatchWhiteListAlert } from '../guard-action/components/DispatchWhiteListAlert'
import { OrderRepair } from '../order-repair'
import { Resolve } from '../resolve'
import { ResolveToggle } from '../resolve/components/ResolveToggle'
import type { WorkflowReportDataI } from '../shared/download-report/types/types'
import { MixpanelDataIProps } from '../shared/types/types'
import { SOP_DRAWER_BREAKPOINT } from '../sop-incident-drawer/components/SOPIncidentDrawer'
import { WORKFLOW_DRAWER_BREAKPOINT, WorkflowButton } from '../workflow-drawer'
import { AdditionalOptionsButton } from './AdditionalOptionsButton'
import { AioEvalResultsButton } from './AioEvalResultsModal'
import { AnimatedPanel } from './AnimatedPanel'
import { IncidentActionTab } from './IncidentActionTab'

interface IncidentActionsIProps {
  hasWorkflows: boolean
  incident: IncidentQueryResult['data']['incident']
  isLoadingWorkflow?: boolean
  isWorkflowFeatureEnabled: boolean
  mixpanelData?: MixpanelDataIProps
  refetchFeedItems?: () => void
  workflowReportData?: WorkflowReportDataI
  workflowStatus: IncidentWorkflowStatus | null
}

export const IncidentActions = ({
  hasWorkflows,
  incident,
  isLoadingWorkflow = false,
  isWorkflowFeatureEnabled,
  mixpanelData,
  refetchFeedItems,
  workflowReportData,
  workflowStatus,
}: IncidentActionsIProps) => {
  const dispatch = useAppDispatch()
  const mode = useLightMode()
  const sopDrawerState = useAppSelector(selectSOPIncidentDrawerState)
  const [width] = useWindowSize()
  const isSOPDisabled = width < SOP_DRAWER_BREAKPOINT

  // Show the workflow button whenever the feature is on. Ownership/status
  // gating happens inside the drawer (greyed-out read-only state); the button
  // stays visible-but-disabled when no workflow is assigned so operators can
  // discover the feature.
  const shouldShowWorkflowButton = isWorkflowFeatureEnabled

  const isWorkflowTooNarrow = width < WORKFLOW_DRAWER_BREAKPOINT
  const isWorkflowDisabled = isWorkflowTooNarrow || !hasWorkflows

  // Resolving is blocked only while the incident-response-workflow feature is on
  // and a workflow is still in progress.
  const isResolveBlockedByWorkflow =
    isWorkflowFeatureEnabled &&
    workflowStatus === IncidentWorkflowStatus.InProgress

  const workflowTooltipLabel = isWorkflowTooNarrow
    ? 'A wider screen is required for Workflow functionality'
    : !hasWorkflows
    ? 'Workflow for this incident type has not been configured yet.'
    : undefined

  const isResolverIntegrationEnabled = useFeatureFlag(
    FeatureFlag.resolverIntegration
  )
  const isIncidentDrawerRebrand = useFeatureFlag(
    FeatureFlag.incidentDrawerRebrand
  )

  const { isInternalImplementer, shouldEnableSendToResolver } = usePermissions()
  const shouldShowSendToResolver =
    isResolverIntegrationEnabled && shouldEnableSendToResolver
  const { setIncidentReal, isLoading: isUpdatingIncidentReal } =
    useSetIsIncidentReal()

  const gridTesting = useGridTesting(incident)

  const {
    isOpen: isAssociateCaseModalOpen,
    onOpen: onAssociateCaseModalOpen,
    onClose: onAssociateCaseModalClose,
  } = useDisclosure()

  const {
    tab,
    handleAddNoteClick,
    handleDispatchClick,
    handleRadioClick,
    handleOrderRepairClick,
    handleResolveClick,
    handleSOPClick,
    closeTabs,
  } = useTabs({ mixpanelData, incidentId: incident?.id })

  const onToggleChange = (real: boolean) => {
    setIncidentReal(incident?.id, real, true, mixpanelData)
  }

  const onActionComplete = () => {
    refetchFeedItems()
    closeTabs()
  }

  const {
    isEmergencyCallEnabled,
    isAddNoteEnabled,
    isDispatchEnabled,
    isRadioGuardsEnabled,
    isOrderRepairEnabled,
    isResolveEnabled,
    isDownloadReportEnabled,
    isSendToOnticEnabled,
    isSendToResolverEnabled,
    isAdditionalOptionsEnabled,
  } = useAvailableIncidentActions({ incident })
  const isDownloadReportBlockedByWorkflowLoading =
    isDownloadReportEnabled && hasWorkflows && isLoadingWorkflow
  const isDownloadReportActionEnabled =
    isDownloadReportEnabled && !isDownloadReportBlockedByWorkflowLoading
  const downloadReportTooltipLabel = isDownloadReportBlockedByWorkflowLoading
    ? 'Workflow data is still loading. Please try again shortly.'
    : undefined
  const shouldShowMoreMenu =
    isAdditionalOptionsEnabled ||
    shouldShowSendToResolver ||
    gridTesting.isEnabled

  return (
    <Box paddingTop={4} paddingX={6} pb='0'>
      <Box
        alignItems='center'
        display='flex'
        flexWrap='wrap'
        justifyContent='space-between'
        mb='15px'
      >
        {shouldShowWorkflowButton && (
          <DarkProvider>
            <WorkflowButton
              colorScheme={mode as 'dark' | 'light'}
              disabled={isWorkflowDisabled}
              onClick={() => {
                dispatch(closeSOPIncidentDrawer())
                dispatch(closeTraceLogDrawer())
                dispatch(toggleWorkflowDrawer())
              }}
              tooltipLabel={
                isWorkflowDisabled ? workflowTooltipLabel : undefined
              }
            />
          </DarkProvider>
        )}

        <Box display='flex' flexWrap='wrap' gap='8px' ml='auto'>
          {incident?.status === IncidentStatus.Resolved && (
            <ResolveToggle
              isLoading={isUpdatingIncidentReal}
              isReal={incident?.real}
              mixpanelData={mixpanelData}
              onChange={onToggleChange}
            />
          )}
          {isAddNoteEnabled && (
            <IncidentActionTab
              icon={<NoteIcon size={18} />}
              isSelected={tab === IncidentTab.AddNote}
              label='Add Note'
              onClick={handleAddNoteClick}
            />
          )}
          <IncidentActionTab
            disabled={isSOPDisabled}
            icon={<SOPIcon size={18} />}
            isSelected={sopDrawerState?.isOpen === true}
            label='Standard Procedures'
            onClick={() => {
              handleSOPClick()
              dispatch(closeTraceLogDrawer())
              dispatch(closeWorkflowDrawer())
              dispatch(toggleSOPIncidentDrawer())
            }}
          />
          {(isDispatchEnabled || isRadioGuardsEnabled) && (
            <GuardActionTab
              isDispatchEnabled={isDispatchEnabled}
              isRadioEnabled={isRadioGuardsEnabled}
              isSelected={
                tab === IncidentTab.DispatchGuards ||
                tab === IncidentTab.RadioGuards
              }
              onDispatchClick={handleDispatchClick}
              onRadioClick={handleRadioClick}
              tooltipText='Assign Guards'
            />
          )}
          {isOrderRepairEnabled && (
            <IncidentActionTab
              icon={<BiWrench size={18} />}
              isSelected={tab === IncidentTab.OrderRepair}
              label='Order Repair'
              onClick={handleOrderRepairClick}
            />
          )}
          {isEmergencyCallEnabled && (
            <EmergencyCallButton
              incident={incident}
              mixpanelData={mixpanelData}
            />
          )}
          {isResolveEnabled && (
            <IncidentActionTab
              disabled={isResolveBlockedByWorkflow}
              icon={<ResolveIcon size={18} />}
              isSelected={tab === IncidentTab.Resolve}
              label='Resolve'
              onClick={handleResolveClick}
              tooltipLabel={
                isResolveBlockedByWorkflow
                  ? 'Cannot resolve until workflow complete.'
                  : undefined
              }
            />
          )}
          {isInternalImplementer && (
            <AioEvalResultsButton incidentId={incident?.id} variant='action' />
          )}
          {shouldShowMoreMenu && (
            <AdditionalOptionsButton
              downloadReportTooltipLabel={downloadReportTooltipLabel}
              incident={incident}
              isDownloadReportEnabled={isDownloadReportActionEnabled}
              isSendToOnticEnabled={isSendToOnticEnabled}
              isSendToResolverEnabled={isSendToResolverEnabled}
              onAssociateCaseModalOpen={onAssociateCaseModalOpen}
              onGridTestingOpen={
                gridTesting.isEnabled
                  ? () => {
                      gridTesting.trackTailgateEvent(GTZ_ENTER)
                      gridTesting.open()
                    }
                  : undefined
              }
              shouldShowDownloadReport={isAdditionalOptionsEnabled}
              tooltipText='More'
              workflowReportData={workflowReportData}
            />
          )}
        </Box>
      </Box>
      <Box>
        {tab == IncidentTab.AddNote && (
          <AnimatedPanel>
            <AddNote
              incidentId={incident?.id}
              mixpanelData={mixpanelData}
              onActionComplete={closeTabs}
            />
          </AnimatedPanel>
        )}
        {tab == IncidentTab.DispatchGuards && (
          <AnimatedPanel>
            {isIncidentDrawerRebrand ? (
              <>
                <DispatchWhiteListAlert incident={incident} />
                <DarkGuardPanel
                  colorScheme={mode}
                  incident={incident}
                  mixpanelData={mixpanelData}
                  onActionComplete={onActionComplete}
                />
              </>
            ) : (
              <DispatchGuards
                incident={incident}
                mixpanelData={mixpanelData}
                onActionComplete={onActionComplete}
              />
            )}
          </AnimatedPanel>
        )}
        {tab == IncidentTab.RadioGuards && (
          <AnimatedPanel>
            {isIncidentDrawerRebrand ? (
              <DarkGuardPanel
                colorScheme={mode}
                incident={incident}
                mixpanelData={mixpanelData}
                mode='radio'
                onActionComplete={onActionComplete}
              />
            ) : (
              <RadioGuards
                facilityId={incident?.facility?.id}
                incidentId={incident?.id}
                mixpanelData={mixpanelData}
                onActionComplete={onActionComplete}
              />
            )}
          </AnimatedPanel>
        )}
        {tab == IncidentTab.OrderRepair && (
          <AnimatedPanel>
            <OrderRepair
              deviceType={gridTesting.device?.type}
              facilityId={incident?.facility?.id}
              incidentId={incident?.id}
              incidentStatus={incident?.status}
              mixpanelData={mixpanelData}
              onActionComplete={onActionComplete}
            />
          </AnimatedPanel>
        )}
        {tab == IncidentTab.Resolve && (
          <AnimatedPanel>
            <Resolve
              incident={incident}
              mixpanelData={mixpanelData}
              onActionComplete={closeTabs}
            />
          </AnimatedPanel>
        )}
      </Box>
      {!isIncidentDrawerRebrand && <Box bgColor='#e2e8f0' h='1px' w='full' />}
      <AssociateCaseModal
        incidentId={incident?.id}
        isOpen={isAssociateCaseModalOpen}
        onClose={onAssociateCaseModalClose}
      />
      {gridTesting.isEnabled &&
        gridTesting.gridCameraId &&
        gridTesting.device?.id && (
          <TailgateGridProvider
            associateDeviceValues={{
              cameraId: gridTesting.gridCameraId,
              cameraName: gridTesting.gridCameraName,
              doorId: gridTesting.device.id,
              doorName: gridTesting.device.name ?? '',
              deviceType: DeviceType.Door,
            }}
            isGridConfigured={gridTesting.isGridConfigured}
            isTailgateGridOpen={gridTesting.isOpen}
            onTailgateGridClose={gridTesting.close}
            shouldRenderDummyPlayer={gridTesting.isOpen}
            trackTailgateEvent={gridTesting.trackTailgateEvent}
          >
            <StandaloneGridTestingModal
              isOpen={gridTesting.isOpen}
              onClose={gridTesting.close}
              presetIncident={gridTesting.presetIncident}
            />
          </TailgateGridProvider>
        )}
    </Box>
  )
}
